import { createI18n } from 'vue-i18n';
import { et as etDateFns, enUS as enDateFns } from 'date-fns/locale';
import et from './locales/et.json';
import en from './locales/en.json';

// Locales the app ships translations for. `et` is the source/fallback language.
export const SUPPORTED_LOCALES = ['et', 'en'];
export const FALLBACK_LOCALE = 'et';

// Maps the numeric `language_id` returned by the API (on the `user` object at
// login) to an app locale code. Extend this as the backend adds languages.
const LANGUAGE_ID_TO_LOCALE = {
    0: 'et', // Estonian (default)
    1: 'en', // English
};

const STORAGE_KEY = 'locale';

/** Narrow any value to a supported locale, or null if unsupported. */
function normalizeLocale(code) {
    if (!code) return null;
    const short = String(code).toLowerCase().split('-')[0];
    return SUPPORTED_LOCALES.includes(short) ? short : null;
}

/** Translate an API `language_id` to a supported locale code (or null). */
export function localeFromLanguageId(languageId) {
    return normalizeLocale(LANGUAGE_ID_TO_LOCALE[languageId]);
}

/**
 * Resolve the locale to boot with, in priority order:
 *   1. user.language_id (persisted from the last login)
 *   2. explicitly stored `locale`
 *   3. browser language
 *   4. fallback (et)
 */
function resolveInitialLocale() {
    try {
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        const fromUser = localeFromLanguageId(user?.language_id);
        if (fromUser) return fromUser;
    } catch {
        // ignore malformed user blob
    }

    const stored = normalizeLocale(localStorage.getItem(STORAGE_KEY));
    if (stored) return stored;

    const fromBrowser = normalizeLocale(navigator.language);
    if (fromBrowser) return fromBrowser;

    return FALLBACK_LOCALE;
}

const i18n = createI18n({
    legacy: false,          // Composition mode...
    globalInjection: true,  // ...but still expose $t in Options-API templates
    locale: resolveInitialLocale(),
    fallbackLocale: FALLBACK_LOCALE,
    messages: { et, en },
});

/** The current locale code (e.g. for binding to date pickers / date-fns). */
export function currentLocale() {
    return i18n.global.locale.value;
}

const DATE_FNS_LOCALES = {
    et: etDateFns,
    en: enDateFns,
};

/** The date-fns locale object matching the active app locale. */
export function dateFnsLocale() {
    return DATE_FNS_LOCALES[currentLocale()] || etDateFns;
}

/** Switch the active locale and persist the choice. No-op if unsupported. */
export function setLocale(code) {
    const next = normalizeLocale(code);
    if (!next) return;
    i18n.global.locale.value = next;
    localStorage.setItem(STORAGE_KEY, next);
    document.documentElement.setAttribute('lang', next);
}

/** Apply the locale implied by a freshly-logged-in user's `language_id`. */
export function setLocaleFromUser(user) {
    const code = localeFromLanguageId(user?.language_id);
    if (code) setLocale(code);
}

// Keep <html lang> in sync with the boot locale.
document.documentElement.setAttribute('lang', currentLocale());

export default i18n;
